import { Map } from 'immutable';
import React, { useEffect, useMemo } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useAppSelector, useAppDispatch } from 'flow/hooks/useRedux';
import { generatePath, useHistory, useParams } from 'react-router-dom';

import {
    getChatSessionDetails,
    getSessionContent,
    getSessionContentHistory,
} from 'flow/actions/chat-actions';
import Page from 'flow/components/Setting/Page';
import useAppContext from 'flow/hooks/useAppContext';
import { ChatSessionKey } from 'flow/types/EntityKeys';
import { selectSessionPosts } from 'flow/selectors/chat-message-selectors';

import { routes } from 'flow/types/routes';
import ChatPostSummary from './messages/ChatPostSummary';
import MessagesPostSkeleton from 'flow/components/loaders/MessagesPostSkeleton';
import { ChatContent } from 'flow/utils/message-utils';

const MessagesPostsPage = () => {
    const { i18next } = useAppContext();
    const { id } = useParams<{ id: ChatSessionKey }>();
    const dispatch = useAppDispatch();
    const history = useHistory();

    const fileDetails = useAppSelector((state) =>
        state.chats.getIn(['details', id], Map()),
    );

    const storedPosts = useAppSelector((state) =>
        selectSessionPosts(state, id),
    );
    const posts = useMemo(() => storedPosts?.reverse(), [storedPosts]);

    useEffect(() => {
        dispatch(getChatSessionDetails(id));
        dispatch(getSessionContent(id, 'posts'));
    }, [dispatch, id]);

    return (
        <Page
            header={i18next.t('Chat.Details.Posts')}
            onClickBack={() =>
                history.push(
                    generatePath(routes['messages.id.settings'], { id }),
                )
            }
            mainId="main-scrollable-div"
        >
            <InfiniteScroll
                dataLength={posts.size || 0}
                next={() => {
                    dispatch(
                        getSessionContentHistory(
                            id,
                            posts.last()?.get('key'),
                            ChatContent.POSTS,
                        ),
                    );
                }}
                hasMore={posts.size < fileDetails?.get('numberOfPosts')}
                loader={<MessagesPostSkeleton isLarge={true} count={4} />}
                scrollableTarget="main-scrollable-div"
            >
                {posts
                    .map((post) => (
                        <ChatPostSummary
                            key={post.get('key')}
                            sessionKey={id}
                            message={post}
                        />
                    ))
                    .toArray()}
            </InfiniteScroll>
        </Page>
    );
};

export default MessagesPostsPage;
